import React, { useRef, useState, useEffect } from 'react'
import {
  Box, Button, Typography, CircularProgress, Alert, LinearProgress,
  Chip, TextField, Divider, Stack, IconButton
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { useMutation, useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { INITIATE_AI_MENU_UPLOAD, CREATE_ADVANCED_MENU } from '../../apollo/advancedMenuMutations'
import { GET_UPLOAD_STATUS, GET_RESTAURANT_ITEMS, GET_ADVANCED_MENUS } from '../../apollo/advancedMenuQueries'

// Step 1 = menu creation form, Step 2 = file upload, Step 3 = processing/result
const STEP_MENU = 'MENU'
const STEP_UPLOAD = 'UPLOAD'
const STEP_PROCESSING = 'PROCESSING'

const AIFileUpload = ({ restaurantId, onParseComplete, existingMenuId, existingMenuName }) => {
  const { t } = useTranslation()
  const fileInputRef = useRef(null)

  // When targeting an existing menu, skip the "create menu" step and upload
  // straight into that menu (creates missing items + links them to it).
  const targetingExisting = Boolean(existingMenuId)

  // Step state
  const [step, setStep] = useState(targetingExisting ? STEP_UPLOAD : STEP_MENU)
  const [createdMenuId, setCreatedMenuId] = useState(existingMenuId || null)

  // Menu form fields
  const [menuName, setMenuName] = useState('')
  const [menuStartTime, setMenuStartTime] = useState('')
  const [menuEndTime, setMenuEndTime] = useState('')
  const [menuError, setMenuError] = useState('')

  // Upload state — supports multiple images, or a single PDF.
  const [selectedFiles, setSelectedFiles] = useState([])
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState('')
  const [uploadId, setUploadId] = useState(null)
  const [logs, setLogs] = useState([])
  const lastProgressRef = useRef('')
  const logBoxRef = useRef(null)
  const pollIntervalRef = useRef(null)

  const refetchMenus = [{ query: GET_ADVANCED_MENUS, variables: { restaurantId } }]

  const [createMenu, { loading: creatingMenu }] = useMutation(CREATE_ADVANCED_MENU, {
    refetchQueries: refetchMenus,
    onError: (err) => setMenuError(err.message),
  })

  // Poll for processing status
  const { data: statusData, refetch: refetchStatus } = useQuery(GET_UPLOAD_STATUS, {
    variables: { uploadId },
    skip: !uploadId,
    fetchPolicy: 'network-only',
  })

  const status = statusData?.advancedMenuUploadStatus?.aiProcessingStatus
  const itemsImported = statusData?.advancedMenuUploadStatus?.itemsImported ?? 0
  const itemsFailed = statusData?.advancedMenuUploadStatus?.itemsFailed ?? 0

  useEffect(() => {
    if (!uploadId) return
    setLogs([])
    lastProgressRef.current = ''

    const interval = setInterval(async() => {
      const result = await refetchStatus()
      const uploadData = result?.data?.advancedMenuUploadStatus
      const currentStatus = uploadData?.aiProcessingStatus
      const newProgress = uploadData?.progressMessage

      if (newProgress && newProgress !== lastProgressRef.current) {
        lastProgressRef.current = newProgress
        setLogs(prev => {
          const updated = [...prev, newProgress]
          setTimeout(() => {
            if (logBoxRef.current) logBoxRef.current.scrollTop = logBoxRef.current.scrollHeight
          }, 50)
          return updated
        })
      }

      if (currentStatus === 'COMPLETED' || currentStatus === 'FAILED') {
        clearInterval(interval)
        pollIntervalRef.current = null
        if (onParseComplete) onParseComplete(uploadData)
      }
    }, 3000)

    pollIntervalRef.current = interval
    return () => clearInterval(interval)
  }, [uploadId])

  useEffect(() => {
    return () => { if (pollIntervalRef.current) clearInterval(pollIntervalRef.current) }
  }, [])

  const [initiateAIUpload] = useMutation(INITIATE_AI_MENU_UPLOAD, {
    refetchQueries: [{ query: GET_RESTAURANT_ITEMS, variables: { restaurantId } }],
    onCompleted: (data) => {
      setUploading(false)
      const id = data?.initiateAIMenuUpload?._id
      if (id) {
        setUploadId(id)
        setStep(STEP_PROCESSING)
      }
    },
    onError: (err) => {
      setUploadError(err.message)
      setUploading(false)
    }
  })

  const handleCreateMenu = async() => {
    if (!menuName.trim()) {
      setMenuError(t('Menu name is required'))
      return
    }
    setMenuError('')
    try {
      const result = await createMenu({
        variables: {
          restaurantId,
          input: {
            name: menuName.trim(),
            menuType: 'ALL_DAY',
            startTime: menuStartTime || null,
            endTime: menuEndTime || null,
          }
        }
      })
      const newMenuId = result?.data?.createAdvancedMenu?._id
      if (newMenuId) {
        setCreatedMenuId(newMenuId)
        setStep(STEP_UPLOAD)
      }
    } catch (e) {
      // error handled by onError above
    }
  }

  const MAX_IMAGE_BYTES = 15 * 1024 * 1024 // 15 MB per image
  const MAX_PDF_BYTES = 50 * 1024 * 1024 // 50 MB for multi-page PDFs

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || [])
    if (fileInputRef.current) fileInputRef.current.value = ''
    if (files.length === 0) return

    // Merge with any already-picked images (lets the user add more).
    const combined = [...selectedFiles, ...files]

    const pdfs = combined.filter(f => f.type.includes('pdf') || f.name.toLowerCase().endsWith('.pdf'))
    if (pdfs.length > 0 && combined.length > 1) {
      setUploadError(t('Upload either one PDF OR multiple images — not both.'))
      return
    }
    const tooBig = combined.find(f => {
      const isPdf = f.type.includes('pdf') || f.name.toLowerCase().endsWith('.pdf')
      return f.size > (isPdf ? MAX_PDF_BYTES : MAX_IMAGE_BYTES)
    })
    if (tooBig) {
      const isPdf = tooBig.type.includes('pdf') || tooBig.name.toLowerCase().endsWith('.pdf')
      setUploadError(t('"{{name}}" is too large ({{size}} MB). Maximum size is {{limit}} MB.', {
        name: tooBig.name,
        size: (tooBig.size / (1024 * 1024)).toFixed(1),
        limit: isPdf ? 50 : 15,
      }))
      return
    }
    setSelectedFiles(combined)
    setUploadError('')
  }

  const removeSelectedFile = (idx) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== idx))
    setUploadError('')
  }

  const handleUpload = async() => {
    if (selectedFiles.length === 0) {
      setUploadError(t('Please select a file first'))
      return
    }
    setUploading(true)
    setUploadError('')
    setUploadId(null)

    try {
      const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:8001'

      // Upload every selected file and collect their URLs.
      const urls = []
      for (const file of selectedFiles) {
        const formData = new FormData()
        formData.append('image', file)
        const uploadRes = await fetch(`${SERVER_URL}/upload/image`, { method: 'POST', body: formData })
        const uploadData = await uploadRes.json().catch(() => ({}))
        if (!uploadRes.ok) throw new Error(uploadData.error || `Upload failed: ${uploadRes.statusText}`)
        if (!uploadData.url) throw new Error('Upload failed — no URL returned')
        urls.push(uploadData.url)
      }

      const isPdf = selectedFiles[0].type.includes('pdf') || selectedFiles[0].name.toLowerCase().endsWith('.pdf')
      const uploadMethod = isPdf ? 'AI_PDF' : 'AI_IMAGE'

      await initiateAIUpload({
        // Multiple image URLs are sent newline-joined in fileUrl.
        variables: { restaurantId, uploadMethod, fileUrl: urls.join('\n'), menuId: createdMenuId }
      })

      setSelectedFiles([])
      if (fileInputRef.current) fileInputRef.current.value = ''
    } catch (err) {
      setUploadError(err.message || t('Upload failed'))
      setUploading(false)
    }
  }

  const handleReset = () => {
    setStep(targetingExisting ? STEP_UPLOAD : STEP_MENU)
    setCreatedMenuId(existingMenuId || null)
    setMenuName('')
    setMenuStartTime('')
    setMenuEndTime('')
    setMenuError('')
    setUploadId(null)
    setSelectedFiles([])
    setUploadError('')
    setLogs([])
    lastProgressRef.current = ''
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  // ── Step 1: Menu creation form ──────────────────────────────────────
  if (step === STEP_MENU) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <MenuBookIcon sx={{ color: '#7c3aed', fontSize: 20 }} />
          <Typography variant="body2" fontWeight={600} sx={{ color: '#344767' }}>
            {t('Step 1: Create a Menu')}
          </Typography>
        </Box>

        {menuError && (
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setMenuError('')}>
            {menuError}
          </Alert>
        )}

        <Stack spacing={1.5}>
          <TextField
            label={t('Menu Name')}
            placeholder={t('e.g. Breakfast, Lunch, Dinner')}
            value={menuName}
            onChange={e => setMenuName(e.target.value)}
            size="small"
            fullWidth
            required
          />
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField
              type="time"
              label={t('Start Time')}
              value={menuStartTime}
              onChange={e => setMenuStartTime(e.target.value)}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText={t('Optional — leave blank for all day')}
            />
            <TextField
              type="time"
              label={t('End Time')}
              value={menuEndTime}
              onChange={e => setMenuEndTime(e.target.value)}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText={t('Optional — leave blank for all day')}
            />
          </Box>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Button
          variant="contained"
          endIcon={creatingMenu ? <CircularProgress size={16} color="inherit" /> : <ArrowForwardIcon />}
          onClick={handleCreateMenu}
          disabled={creatingMenu || !menuName.trim()}
          size="small"
          sx={{ bgcolor: '#7c3aed', borderRadius: '999px', textTransform: 'none', fontWeight: 700, px: 2.5, transition: 'all .15s ease', '&:hover': { bgcolor: '#6d28d9' } }}
        >
          {creatingMenu ? t('Creating Menu...') : t('Create Menu & Continue')}
        </Button>
      </Box>
    )
  }

  // ── Step 2: File upload ─────────────────────────────────────────────
  if (step === STEP_UPLOAD) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 18 }} />
          <Typography variant="body2" sx={{ color: '#4caf50', fontWeight: 600 }}>
            {targetingExisting ? t('Uploading into:') : t('Menu created:')} <strong>{existingMenuName || menuName}</strong>
          </Typography>
        </Box>

        <Typography variant="body2" fontWeight={600} sx={{ color: '#344767', mb: 1.5 }}>
          {t('Upload Your Menu Image or PDF')}
        </Typography>
        {targetingExisting && (
          <Typography variant="caption" sx={{ color: '#7b809a', display: 'block', mb: 1.5 }}>
            {t('Missing items will be created and added to this menu. Existing items will just be added.')}
          </Typography>
        )}

        {uploadError && (
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setUploadError('')}>
            {uploadError}
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.heic,.heif,.pdf"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <Button
            variant="outlined"
            startIcon={<InsertDriveFileIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            size="small"
          >
            {selectedFiles.length > 0 ? t('Add More') : t('Choose Files')}
          </Button>
          <Button
            variant="contained"
            startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
            onClick={handleUpload}
            disabled={selectedFiles.length === 0 || uploading}
            size="small"
            sx={{ bgcolor: '#7c3aed', borderRadius: '999px', textTransform: 'none', fontWeight: 700, px: 2.5, transition: 'all .15s ease', '&:hover': { bgcolor: '#6d28d9' } }}
          >
            {uploading ? t('Uploading...') : t('Upload & Scan')}
          </Button>
        </Box>

        {selectedFiles.length > 0 && (
          <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {selectedFiles.map((f, idx) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 280 }}>
                  {f.name} ({(f.size / (1024 * 1024)).toFixed(1)} MB)
                </Typography>
                {!uploading && (
                  <IconButton size="small" onClick={() => removeSelectedFile(idx)} sx={{ p: 0.25 }}>
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>
        )}

        <Typography variant="caption" sx={{ color: '#7b809a', mt: 1, display: 'block' }}>
          {t('Supports multiple photos (JPG, PNG, HEIC) or a single PDF. Max size 15 MB per file. AI will extract all items, prices, and categories automatically.')}
        </Typography>
      </Box>
    )
  }

  // ── Step 3: Processing / result ─────────────────────────────────────
  return (
    <Box>
      {(status === 'PENDING' || (!status && uploadId)) && (
        <Box sx={{ py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" sx={{ color: '#344767', fontWeight: 500 }}>
              {t('Queued for AI processing...')}
            </Typography>
          </Box>
          <LinearProgress sx={{ borderRadius: 1 }} />
        </Box>
      )}

      {status === 'PROCESSING' && (
        <Box sx={{ py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <AutoAwesomeIcon sx={{ color: '#7c3aed', fontSize: 20, animation: 'spin 2s linear infinite', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }} />
            <Typography variant="body2" sx={{ color: '#7c3aed', fontWeight: 600 }}>
              {t('AI is working...')}
            </Typography>
            <CircularProgress size={14} color="secondary" sx={{ ml: 'auto' }} />
          </Box>
          <LinearProgress color="secondary" sx={{ borderRadius: 1, mb: 1.5 }} />
          <Box
            ref={logBoxRef}
            sx={{ bgcolor: '#0d0d0d', borderRadius: '12px', px: 1.5, py: 1, maxHeight: 180, overflowY: 'auto', fontFamily: 'monospace', border: '1px solid #27272a' }}
          >
            {logs.length === 0 && (
              <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>
                Waiting for AI response...
              </Typography>
            )}
            {logs.map((msg, i) => (
              <Typography key={i} variant="caption" sx={{ color: i === logs.length - 1 ? '#a5d6a7' : '#9e9e9e', display: 'block', lineHeight: 1.8, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.72rem' }}>
                <span style={{ color: '#616161', marginRight: 6 }}>[{String(i + 1).padStart(2, '0')}]</span>{msg}
              </Typography>
            ))}
            {logs.length > 0 && (
              <Typography variant="caption" sx={{ color: '#7c3aed', display: 'inline-block', animation: 'blink 1s step-end infinite', '@keyframes blink': { '50%': { opacity: 0 } } }}>
                ▌
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {status === 'COMPLETED' && (
        <Box sx={{ py: 1 }}>
          <Alert icon={<CheckCircleIcon />} severity="success" sx={{ mb: 1.5 }}>
            <Typography variant="body2" fontWeight="bold">
              {t('Menu imported successfully!')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
              <Chip label={`${itemsImported} ${t('items created')}`} size="small" color="success" variant="outlined" />
              {itemsFailed > 0 && (
                <Chip label={`${itemsFailed} ${t('failed')}`} size="small" color="warning" variant="outlined" />
              )}
            </Box>
          </Alert>
          <Typography variant="caption" sx={{ color: '#7b809a', display: 'block', mb: 1.5 }}>
            {t('Go to the Items tab to review and edit the imported items.')}
          </Typography>
          <Button size="small" variant="outlined" onClick={handleReset}>
            {t('Upload Another Menu')}
          </Button>
        </Box>
      )}

      {status === 'FAILED' && (
        <Box sx={{ py: 1 }}>
          <Alert icon={<ErrorIcon />} severity="error" sx={{ mb: 1.5 }}>
            {t('AI could not process this menu. Please try with a clearer image.')}
          </Alert>
          <Button size="small" variant="outlined" onClick={handleReset}>
            {t('Try Again')}
          </Button>
        </Box>
      )}
    </Box>
  )
}

export default AIFileUpload
